import { LevelOfProtectionType } from "../addLegalEntityDialog/AddLegalEntityDialog.types.ts";
import { ControllerProcess } from "../../processingActivity/processingActivityOverview/ProcessingActivityOverview.types";
import { ListFilter, TextFilter } from "@common/api/filterTypes.ts";
import { RiskLevel } from "../legalEntityClassificationTab/LegalEntityClassificationTab.types";
import { LegalEntityAuditStatus } from "../legalEntityAuditTab/LegalEntityAudit.types";
import { EntityFieldType } from "@common/entityField.types";
import { LegalEntityColumn } from "./legalEntityColumns";
import { LegalEntityAuditFrequency, LegalEntityAuditType } from "../LegalEntity.types";

// Mirrors Core/Filtering/LegalEntities/LegalEntityScope.cs. A legal entity is either owned by the customer
// or it is not, and that is the only split the lists, the detail pages and their urls are built around.
export enum LegalEntityScope {
    External = 1,
    GroupEntity = 2,
}

export function toLegalEntityScope(isCustomerOwned: boolean) {
    return isCustomerOwned ? LegalEntityScope.GroupEntity : LegalEntityScope.External;
}

export interface LegalEntitySearchRequest {
    filter: LegalEntityFilterRequest;
    displayFields: Array<LegalEntityFieldOrderItem>;
}

// Mirrors Core/Filtering/LegalEntities/LegalEntityTableRequest.cs. A displayed field is either one of the
// built-in columns or one of the custom fields the entity's template defines; the two interleave freely.
export interface LegalEntityFieldOrderItem {
    column?: LegalEntityColumn;
    customFieldId?: string;
    displayOrder: number;
}

export interface LegalEntityExportHeader {
    header: string;
    column?: LegalEntityColumn;
    customFieldId?: string;
}

// Mirrors Core/Filtering/LegalEntities/LegalEntityFilterRequest.cs. Scope and searchText sit outside the saved
// view blob: scope is which page you are on, and search is deliberately not persisted with a view.
export interface LegalEntityFilterRequest {
    scope: LegalEntityScope;
    searchText?: string;

    showOnlyMine?: boolean;

    countryCodes?: ListFilter<string>;
    businessAreaIds?: ListFilter<string>;
    complianceAreaIds?: ListFilter<string>;
    certificationIds?: ListFilter<string>;
    responsibleIds?: ListFilter<string>;
    tagIds?: ListFilter<string>;
    types?: ListFilter<string>;
    isActive?: boolean;
    isPreselectedOnCreate?: boolean;

    description?: TextFilter;
    address?: TextFilter;
    zipCode?: TextFilter;
    city?: TextFilter;
    companyNumber?: TextFilter;
    referenceNumber?: TextFilter;
    contacts?: TextFilter;
    note?: TextFilter;
    website?: TextFilter;

    // Set by the Third parties system view and offered in the filter bar as a yes/no.
    isRecipientOnDisclosure?: boolean;

    isApproved?: boolean;
    supervisionTypes?: ListFilter<LegalEntityAuditType>;
    supervisionFrequencies?: ListFilter<LegalEntityAuditFrequency>;

    // What customers' own company lists filter on, so a migrated list keeps narrowing the same way. The counts
    // are "has any" booleans because every list in production used a threshold of 0 or 1.
    legalEntityIds?: ListFilter<string>;
    isTransferImpactAssessmentUnnecessary?: boolean;
    isSenderOnSource?: boolean;
    isSenderOnDataProcessor?: boolean;
    isDataProcessor?: boolean;
    isSubProcessor?: boolean;
    locations?: ListFilter<LevelOfProtectionType>;
    hasDocuments?: boolean;
    documentTypeIds?: ListFilter<string>;
    hasSharings?: boolean;
    roles?: ListFilter<ControllerProcess>;
    hasProcessingActivities?: boolean;
    subContractorIds?: ListFilter<string>;
    isTransferBasisUnnecessary?: boolean;

    // The last of the old list's filterable columns. Data categories and data subjects reach the entity through
    // its processing activities, document owners and group entities through its documents.
    transferBasisIds?: ListFilter<string>;
    documentOwnerIds?: ListFilter<string>;
    documentGroupEntityIds?: ListFilter<string>;
    dataCategoryIds?: ListFilter<string>;
    dataSubjectIds?: ListFilter<string>;
    hasDataCategories?: boolean;
    hasDataSubjects?: boolean;

    classificationQuestionnaireIds?: ListFilter<string>;
    classificationRiskLevels?: ListFilter<RiskLevel>;
    latestAuditQuestionnaireIds?: ListFilter<string>;
    latestAuditStatuses?: ListFilter<LegalEntityAuditStatus>;

    // Each entry is "{riskAreaId}:{riskLevel}", matching the ids the risk area column renders.
    riskAreaLevels?: ListFilter<string>;

    // One entry per filtered custom field, its value read according to the field's own input type. Shaped like
    // the system and contract overviews' custom field filters, which the filter bar already produces.
    customFieldFilters?: Array<CustomFieldFilter>;

    pageNumber: number;
    pageSize: number;
    sortByColumn?: LegalEntityColumn;
    // Set instead of sortByColumn when the sorted column is a custom field, which has no enum value to name it.
    sortByCustomFieldId?: string;
    sortDirection: LegalEntitySortDirection;
}

export interface CustomFieldFilter {
    inputFieldId: string;
    operator: number;
    value: any;
}

export enum LegalEntitySortDirection {
    ASC = 0,
    DESC = 1,
}

export interface LegalEntitySearchResult {
    legalEntities: Array<LegalEntityFilterItem>;
    totalCount: number;
    pageNumber: number;
    pageSize: number;
    totalPages: number;
}

// Mirrors Core/Filtering/LegalEntities/LegalEntityFieldValue.cs. A row is an identity plus the requested cells in
// the requested order, rendered by DynamicCellRenderer exactly as the system and contract rows are.
export interface LegalEntityFilterItem {
    id: string;
    name: string;
    isActive: boolean;
    isCustomerOwned: boolean;
    // On the row rather than among the fields because the group entity overview tints a preselected row whether
    // or not the user is showing the column that says so.
    isPreselectedOnCreate: boolean;
    fields: Array<LegalEntityFieldValue>;
}

export interface LegalEntityFieldValue {
    value: any;
    type: EntityFieldType;
    column: LegalEntityColumn;
    // Set instead of column for a cell from one of the template's custom fields.
    customFieldId?: string;
    referencedId?: string;
}

// The value of an EntityFieldType.LegalEntityAuditStatus cell.
export interface LegalEntityAuditStatusValue {
    status: LegalEntityAuditStatus;
    numberOfQuestionsAnswered: number;
    totalNumberOfQuestions: number;
    lastestRecipientUpdate: Date | null;
    showQuestionsStatistics: boolean;
}

export interface LegalEntityAuditInformationsDto {
    legalEntityIds: string[];
    fetchLatestAudit: boolean;
}

// Shared by the legal entity detail pages, which are discriminated by the list the user arrived from. Lives
// here rather than on a component so the detail pages do not have to import from a list page.
export interface ILegalEntitiesProps {
    legalEntityScope: LegalEntityScope;
}

// Travels in the returnUrl query string, so string values keep the URL readable when debugging a detour.
export enum CreateLegalEntityReturnType {
    Asset = "asset",
    Contract = "contract",
    ProcessingActivity = "processingActivity",
}
